import React, { useState, useEffect } from 'react';
import Header from '../../components/navigation/Header';
import GlobalFilterBar from '../../components/navigation/GlobalFilterBar';
import ComplianceAlertBanner from '../../components/navigation/ComplianceAlertBanner';
import DataRefreshIndicator from '../../components/navigation/DataRefreshIndicator';
import LegalDisclaimerBanner from '../../components/navigation/LegalDisclaimerBanner';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import auditTrailService from '../../services/auditTrailService';
import AuditEntriesTable from './components/AuditEntriesTable';
import AuditFilters from './components/AuditFilters';
import AuditStatisticsCard from './components/AuditStatisticsCard';
import AuditExportOptions from './components/AuditExportOptions';
import TeaserEditModal from '../../components/TeaserEditModal';
import { useTeaserEdit } from '../../hooks/useTeaserEdit';
import { billingService } from '../../services/billingService';
import { supabase } from '../../lib/supabase';

const AuditTrail = () => {
  const [entries, setEntries] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [filterOptions, setFilterOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    entityType: '',
    actionType: '',
    performedBy: '',
    dateFrom: '',
    dateTo: '',
    searchTerm: '',
    limit: 100,
    offset: 0
  });
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [subscriptionStatus, setSubscriptionStatus] = useState('active');

  const { 
    editingField: teaserEditField, 
    showRedirectModal, 
    startEdit, 
    handleInput: handleTeaserInput, 
    closeModal,
    isEditable 
  } = useTeaserEdit(subscriptionStatus === 'active');

  useEffect(() => {
    loadFilterOptions();
    checkSubscription();
  }, []);

  useEffect(() => {
    loadAuditEntries();
    loadStatistics();
  }, [filters]);

  const loadFilterOptions = async () => {
    try {
      const options = await auditTrailService?.getFilterOptions();
      setFilterOptions(options);
    } catch (err) {
      console.error('Failed to load filter options:', err);
    }
  };

  const loadAuditEntries = async () => {
    try {
      setLoading(true);
      setError('');

      const result = await auditTrailService?.getAllAuditEntries(filters);
      setEntries(result?.entries || []);
      setTotal(result?.total || 0);
      setHasMore(result?.hasMore || false);
    } catch (err) {
      setError(err?.message || 'Failed to load audit entries');
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      setStatsLoading(true);
      const stats = await auditTrailService?.getAuditStatistics(
        filters?.dateFrom,
        filters?.dateTo
      );
      setStatistics(stats);
    } catch (err) {
      console.error('Failed to load statistics:', err);
    } finally {
      setStatsLoading(false);
    }
  };

  const checkSubscription = async () => {
    try {
      const { data: user } = await supabase?.auth?.getUser();
      if (user?.user?.id) {
        const { data: companySettings } = await supabase?.from('company_settings')?.select('id')?.eq('user_id', user?.user?.id)?.single();
        
        if (companySettings?.id) {
          const subscriptionDetails = await billingService?.getSubscriptionDetails(companySettings?.id);
          const status = subscriptionDetails?.subscriptionStatus || 'suspended';
          setSubscriptionStatus(status);
        }
      }
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters({ ...newFilters, offset: 0 });
  };

  const handleResetFilters = () => {
    setFilters({
      entityType: '',
      actionType: '',
      performedBy: '',
      dateFrom: '',
      dateTo: '',
      searchTerm: '',
      limit: 100,
      offset: 0
    });
  };

  const handleLoadMore = () => {
    setFilters(prev => ({
      ...prev,
      offset: prev?.offset + prev?.limit
    }));
  };

  const handleRefresh = () => {
    loadAuditEntries();
    loadStatistics();
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <GlobalFilterBar onFilterChange={() => {}} />
      <ComplianceAlertBanner />
      <DataRefreshIndicator />
      <LegalDisclaimerBanner />

      <main className="pt-32 pb-12 px-4 md:px-6 lg:px-8 max-w-7xl mx-auto">
        {/* Page Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="History" size={24} color="var(--color-primary)" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground">
                  Audit Trail Viewer
                </h1>
                <p className="caption text-sm text-muted-foreground mt-1">
                  Comprehensive tracking of all system actions, user changes, approvals, and DLIR submissions
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="default"
              onClick={handleRefresh}
              iconName="RefreshCw"
              iconPosition="left"
            >
              Refresh
            </Button>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-error/10 border border-error rounded-lg">
            <div className="flex items-center gap-2">
              <Icon name="AlertCircle" size={20} color="var(--color-error)" />
              <p className="caption text-sm text-error">{error}</p>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="mb-6">
          <AuditFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            filterOptions={filterOptions}
            onReset={handleResetFilters}
          />
        </div>

        {/* Statistics and Export */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <div className="lg:col-span-2">
            <AuditStatisticsCard statistics={statistics} loading={statsLoading} />
          </div>
          <div>
            <AuditExportOptions filters={filters} />
          </div>
        </div>

        {/* Results Summary */}
        <div className="mb-4 flex items-center justify-between">
          <p className="caption text-sm text-muted-foreground">
            Showing {entries?.length} of {total?.toLocaleString('en-PG')} entries
          </p>
        </div>

        {/* Audit Entries Table */}
        <div className="mb-6">
          <AuditEntriesTable entries={entries} loading={loading} />
        </div>

        {/* Load More */}
        {hasMore && (
          <div className="flex justify-center">
            <Button
              variant="outline"
              size="default"
              onClick={handleLoadMore}
              loading={loading}
              iconName="ChevronDown"
              iconPosition="right"
            >
              Load More
            </Button>
          </div>
        )}
      </main>

      <TeaserEditModal isOpen={showRedirectModal} onClose={closeModal} />
    </div>
  );
};

export default AuditTrail;